import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createPatient,
  createPatientTreatment,
  createTreatmentStage,
  deletePatient,
  deletePatientToothSpacingMap,
  deletePatientTreatment,
  deleteTreatmentRecommendationLetter,
  deleteTreatmentStage,
  deleteTreatmentStageVisualizationMedia,
  reorderTreatmentStageVisualizationMedia,
  updateCycleStatus,
  updatePatient,
  updatePatientTreatment,
  updateTreatmentStage,
  updateTreatmentStageStep,
  uploadPatientToothSpacingMap,
  uploadTreatmentRecommendationLetter,
  uploadTreatmentStageVisualizationMedia,
  type CreatePatientPayload,
  type CreateTreatmentStagePayload,
  type CreateTreatmentPayload,
  type UpdatePatientPayload,
  type UpdateTreatmentStagePayload,
  type UpdateTreatmentStageStepPayload,
  type UpdateTreatmentPayload,
} from '../../../api/patients';
import { useAuth } from '../../auth/AuthProvider';

async function invalidatePatientQueries(queryClient: ReturnType<typeof useQueryClient>, patientId?: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['patients'] }),
    ...(patientId
      ? [
          queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
          queryClient.invalidateQueries({ queryKey: ['treatments', patientId] }),
        ]
      : []),
  ]);
}

export function useCreatePatient() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePatientPayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return createPatient(payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient);
    },
  });
}

export function useUpdatePatient(patientId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdatePatientPayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return updatePatient(patientId, payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useCreatePatientTreatment(patientId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTreatmentPayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return createPatientTreatment(patientId, payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUpdatePatientTreatment(patientId: string, treatmentId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateTreatmentPayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return updatePatientTreatment(patientId, treatmentId, payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useCreateTreatmentStage(patientId: string, treatmentId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTreatmentStagePayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return createTreatmentStage(patientId, treatmentId, payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUpdateTreatmentStage(patientId: string, treatmentId: string, stageId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateTreatmentStagePayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return updateTreatmentStage(patientId, treatmentId, stageId, payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUpdateCycleStatus(patientId: string, treatmentId: string, cycleId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: 'draft' | 'in_progress' | 'completed' | 'cancelled') => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return updateCycleStatus(patientId, treatmentId, cycleId, status, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUploadTreatmentStageVisualizationMedia(
  patientId: string,
  treatmentId: string,
  stageId: string,
) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return uploadTreatmentStageVisualizationMedia(
        patientId,
        treatmentId,
        stageId,
        file,
        tokens.access_token,
      );
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useDeleteTreatmentStageVisualizationMedia(
  patientId: string,
  treatmentId: string,
  stageId: string,
) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mediaId: string) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return deleteTreatmentStageVisualizationMedia(
        patientId,
        treatmentId,
        stageId,
        mediaId,
        tokens.access_token,
      );
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useReorderTreatmentStageVisualizationMedia(
  patientId: string,
  treatmentId: string,
  stageId: string,
) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: readonly string[]) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return reorderTreatmentStageVisualizationMedia(
        patientId,
        treatmentId,
        stageId,
        orderedIds,
        tokens.access_token,
      );
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUploadTreatmentRecommendationLetter(patientId: string, treatmentId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return uploadTreatmentRecommendationLetter(patientId, treatmentId, file, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useDeleteTreatmentRecommendationLetter(patientId: string, treatmentId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return deleteTreatmentRecommendationLetter(patientId, treatmentId, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUploadPatientToothSpacingMap(patientId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return uploadPatientToothSpacingMap(patientId, file, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useDeletePatientToothSpacingMap(patientId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return deletePatientToothSpacingMap(patientId, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useUpdateTreatmentStageStep(
  patientId: string,
  treatmentId: string,
  stageId: string,
  stepId: string,
) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateTreatmentStageStepPayload) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return updateTreatmentStageStep(patientId, treatmentId, stageId, stepId, payload, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useDeletePatientTreatment(patientId: string, treatmentId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return deletePatientTreatment(patientId, treatmentId, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useDeleteTreatmentStage(patientId: string, treatmentId: string, stageId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return deleteTreatmentStage(patientId, treatmentId, stageId, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient, patientId);
    },
  });
}

export function useDeletePatient() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patientId: string) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return deletePatient(patientId, tokens.access_token);
    },
    onSuccess: async () => {
      await invalidatePatientQueries(queryClient);
    },
  });
}

export const useCreateTreatmentCycle = useCreateTreatmentStage;
export const useUpdateTreatmentCycle = useUpdateTreatmentStage;
export const useUpdateTreatmentCycleStage = useUpdateTreatmentStageStep;
